import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { tournamentPrizePool } from '@/common/config/onboarding-config';
import {
  H1,
  H2,
  StringMain,
  TextXSmall,
} from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import { Timer } from './Timer';

import css from './styles.module.scss';

import usdPng from '/img/icons/usd.png';
import usdWebp from '/img/icons/usd.webp';
import lockPrizePng from '/img/leagues/lock.png';
import lockPrizeWebp from '/img/leagues/lock.webp';
import unlockPrizePng from '/img/leagues/trophy.png';
import unlockPrizeWebp from '/img/leagues/trophy.webp';

interface LeagueBannerProps {
  className?: string;
  activeLeagueRank?: number;
  timeToEnd: number;
}

const LeagueBanner: FC<LeagueBannerProps> = ({
  className,
  activeLeagueRank,
  timeToEnd,
}) => {
  const { t } = useTranslation();
  const hasArenaPrizeLock = (activeLeagueRank || 0) < 2;

  const reward = tournamentPrizePool.usd;

  const title = hasArenaPrizeLock
    ? 'Unlock arena prize pool!'
    : 'Next arena prize pool';

  const description = hasArenaPrizeLock
    ? 'Reach Steel League or higher'
    : 'Rewards every week!';

  const bannerImage = hasArenaPrizeLock
    ? { img: lockPrizePng, webp: lockPrizeWebp, alt: 'lock' }
    : { img: unlockPrizePng, webp: unlockPrizeWebp, alt: 'trophy' };

  return (
    activeLeagueRank !== undefined && (
      <div
        className={cn(
          css.leagueBanner,
          className,
          css[`leagueBanner--${hasArenaPrizeLock ? 'lock' : 'unlock'}`],
        )}
      >
        <div className="flex flex-col items-center">
          <H2 weight="bold" className="mb-2">
            {t('New event starts soon')}
          </H2>
          <Timer timeToEnd={timeToEnd} />
        </div>

        {reward > 0 && (
          <div className="flex gap-3 items-center h-20">
            <Picture className="aspect-square h-full" {...bannerImage} />
            <div className="flex flex-col gap-2">
              <StringMain weight="medium">{t(title)}</StringMain>
              <div className="flex items-center gap-1">
                <Picture
                  className="w-8 h-8"
                  img={usdPng}
                  webp={usdWebp}
                  alt="usd"
                />
                <H1 weight="bold">{reward}</H1>
              </div>
              <TextXSmall
                className={cn(
                  '!leading-1.2',
                  hasArenaPrizeLock
                    ? css.lockRewardDescription
                    : '!text-white/60',
                )}
              >
                {t(description)}
              </TextXSmall>
            </div>
          </div>
        )}
        {/* <div className="w-full h-full relative z-[2] flex items-center justify-between pr-5 pl-4">
          <div className="flex flex-col pt-3 pb-2">
            <StringMain
              weight="bold"
              className={cn('mb-1', !hasArenaPrizeLock && '!text-gold')}
            >
              {t(title)}
            </StringMain>
            <div className="flex items-baseline mb-2">
              <H2 weight="bold">$ {tournamentPrizePool.usd}</H2>
            </div>
            <TextXSmall
              className={cn(
                '!leading-1.2',
                hasArenaPrizeLock ? css.redDescription : '!text-white/60',
              )}
            >
              {t(description)}
            </TextXSmall>
          </div>
          <div className="h-full aspect-square relative shrink-0">
            <Picture
              {...bannerImage}
              className={cn(
                'absolute top-0 left-1/2 -translate-x-1/2 h-full w-auto shrink-0',
                hasArenaPrizeLock ? 'aspect-[133/120]' : 'aspect-[195/160]',
              )}
              alt={hasArenaPrizeLock ? 'lock' : 'prize'}
            />
          </div>
        </div>
        <Picture
          img={bannerPatternPng}
          webp={bannerPatternWebp}
          className={cn('absolute top-0 left-0 w-full')}
          alt="wales"
        />
        <span
          className={cn(
            css.leftCircle,
            hasArenaPrizeLock
              ? 'blur-[120px] bg-white'
              : 'blur-[50px] bg-[#50AF95]',
          )}
        ></span>
        <span
          className={cn(
            css.rightCircle,
            hasArenaPrizeLock
              ? 'blur-[100px] bg-white'
              : 'blur-[60px] bg-[#50AF95]',
          )}
        ></span> */}
      </div>
    )
  );
};

export { LeagueBanner };
